import { Injectable, inject, signal } from '@angular/core';
import { finalize, tap } from 'rxjs';

import {
  BookingRequestApiService,
  ChatMessageApiRequestModel,
  ChatMessageModel,
  ChatMessageTypeEnum,
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventName,
  GoogleAnalyticsService,
} from '@bookly/shared';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  readonly #bookingRequestApiService = inject(BookingRequestApiService);
  readonly #googleAnalyticService = inject(GoogleAnalyticsService);

  readonly #chatMessagesSignal = signal<ChatMessageModel[]>([]);
  readonly #workNotesSignal = signal<ChatMessageModel[]>([]);
  readonly #isMessageLoadingSignal = signal(false);

  get chatMessages() {
    return this.#chatMessagesSignal.asReadonly();
  }

  get workNotes() {
    return this.#workNotesSignal.asReadonly();
  }

  get isMessageLoading() {
    return this.#isMessageLoadingSignal.asReadonly();
  }

  public getMessages(bookingId: number, type: ChatMessageTypeEnum) {
    this.#chatMessagesSignal.set([]);
    this.#isMessageLoadingSignal.set(true);
    return this.#bookingRequestApiService.getMessages(bookingId, type).pipe(
      tap(chatMessages => {
        if (type === ChatMessageTypeEnum.Chat) {
          this.#chatMessagesSignal.set(chatMessages);
        }
        if (type === ChatMessageTypeEnum.WorkNote) {
          this.#workNotesSignal.set(chatMessages);
        }
      }),
      finalize(() => this.#isMessageLoadingSignal.set(false))
    );
  }

  public markAllMessagesAsRead(bookingId: number) {
    return this.#bookingRequestApiService
      .markAllMessagesAsRead(bookingId)
      .pipe(tap(chatMessages => this.#chatMessagesSignal.set(chatMessages)));
  }

  public postMessage(
    message: ChatMessageApiRequestModel,
    type: ChatMessageTypeEnum
  ) {
    this.#isMessageLoadingSignal.set(true);
    return this.#bookingRequestApiService.postMessage(message, type).pipe(
      tap(chatMessages => {
        let eventType = GoogleAnalyticsEventName.MessageSent;
        if (type === ChatMessageTypeEnum.Chat) {
          this.#chatMessagesSignal.set(chatMessages);
        }
        if (type === ChatMessageTypeEnum.WorkNote) {
          eventType = GoogleAnalyticsEventName.WorkNoteSent;
          this.#workNotesSignal.set(chatMessages);
        }
        this.#googleAnalyticService.trackEvent(
          eventType,
          GoogleAnalyticsEventCategory.BookingRequest,
          `Request ID: ${message.bookingId}`
        );
      }),
      finalize(() => this.#isMessageLoadingSignal.set(false))
    );
  }
}
